import { useEffect, useLayoutEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { useUxBehavior } from "@/hooks/useUxBehavior";
import { useFontSize } from "@/hooks/useFontSize";
import { useRecordBrowserTimeZone } from "@/hooks/useRecordBrowserTimeZone";
import { FelixRail } from "@/components/layout/FelixRail";
import { ShelfSidebar } from "@/components/layout/ShelfSidebar";
import { Topbar } from "@/components/layout/Topbar";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";
import { CommandPalette } from "@/components/CommandPalette";
import { ConversationPanel } from "@/components/ConversationPanel";
import { FelixChat } from "@/components/FelixChat";
import { ActivityToasts } from "@/components/ActivityToasts";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PageTransition } from "@/components/motion/PageTransition";
import { KeyboardShortcutsHelp } from "@/components/KeyboardShortcutsHelp";
import { AmbientIntelligenceBar } from "@/components/AmbientIntelligenceBar";
// FelixLauncher (the bottom-right FAB) was removed in Phase 7. The
// canonical Felix entry point is now the "Ask Felix" pill in Topbar.tsx
// on desktop and the Felix tab in MobileBottomNav.tsx on mobile.
// One entry point per device size. See Plan §Phase 7 — "Full demotion".
import { AppOnboardingTour } from "@/components/AppOnboardingTour";
import { useSidebarStore } from "@/stores/sidebar";
import { useFelixPanelStore } from "@/stores/felixPanel";
import {
  useProjectMode,
  shellMode,
  isOrchestratorMode,
  isSidebarSuppressingMode,
} from "@/hooks/useProjectMode";
import { useV3Flag, V3_FLAGS } from "@/lib/featureFlagsV3";
import { recordTelemetry } from "@/lib/telemetry";
import { useUiMode, isFrontier } from "@/stores/uiMode";
import { FrontierBackdrop } from "@/components/layout/FrontierBackdrop";

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const location = useLocation();
  // #589 — always-learning UX behavior tracking (nav funnels, clicks-to-goal,
  // rage/dead-end, Felix-vs-non-Felix cohort). Mounted once at the shell root;
  // fire-and-forget, never affects render.
  useUxBehavior();
  // The user's display text size (Normal / Large / Presentation ...), applied
  // to the document root on every page. Row 4bd890ab.
  useFontSize();
  // Each member's own time zone for the Team page, from their browser, unless
  // they chose one. Row 511fdc25.
  useRecordBrowserTimeZone();
  const collapsed = useSidebarStore((s) => s.collapsed);
  const felixOpen = useFelixPanelStore((s) => s.open);
  const felixWidth = useFelixPanelStore((s) => s.width);
  // Phase 6 — Shelf Sidebar conditional surface (Patent I).
  //   * Iterator / Operate / Broadcast / Upload+Decompose → NO sidebar
  //     (the 3/4 canvas is the work surface). We emit a telemetry
  //     ping so the Phase 7 friction pipeline can distinguish the
  //     intentional absence from a bug.
  //   * Orchestrator (structured / unstructured) AND shelf_sidebar
  //     flag ON → ShelfSidebar.
  //   * Anything else → FelixRail (the Felix-first minimal nav, app-wide).
  const { mode: resolvedMode, projectId } = useProjectMode();
  // Row ab7c4e09: inside a project the Orchestrator rail is the default (shellMode).
  const projectMode = shellMode(resolvedMode, projectId, location.pathname);
  // Universal Classic/Frontier posture — drives the platform-wide ambient skin
  // (FrontierBackdrop) so flipping the toggle changes EVERY page, not just the
  // surfaces that mount a <FrontierLead>.
  const frontierOn = useUiMode((s) => isFrontier(s.mode));
  const { enabled: shelfFlagOn } = useV3Flag(V3_FLAGS.SHELF_SIDEBAR);
  const showShelfSidebar =
    shelfFlagOn && isOrchestratorMode(projectMode);
  const suppressSidebar = isSidebarSuppressingMode(projectMode);

  // Iterator is a design tool — give it the room (Bob 2026-07-23: "work like
  // Figma"). On the Iterator route we drop the left nav + the page padding so
  // the workspace runs edge-to-edge (the topbar stays, so the shell's height
  // math is untouched). Other pages are unaffected.
  const iteratorImmersive = location.pathname.endsWith("/iterator");

  // Patent I (element e) read-back leg: the friction pipeline tunes each user's
  // hover-with-intent dwell (ms) and stores it; this fetches that learned value
  // so the Shelf Sidebar honors it instead of the 200 ms default — closing the
  // per-user self-tuning loop. Only fetched when the shelf is shown; session-
  // stable (the pipeline adjusts at most hourly); failure → undefined → default.
  const { data: uiFriction } = useQuery({
    queryKey: ["me", "ui-friction"],
    queryFn: () =>
      apiClient.get<{ shelf_sidebar_dwell_ms: number }>("/me/ui-friction"),
    enabled: showShelfSidebar,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });

  // Phase 7 friction-pipeline signal — fire once per mode change so
  // the absence of any sidebar in Iterator/Operate is observable.
  useEffect(() => {
    if (suppressSidebar) {
      void recordTelemetry("iterator.sidebar_dismissed_by_iterator_mode", {
        project_mode: projectMode ?? "unknown",
      });
    }
  }, [suppressSidebar, projectMode]);
  // KI-0195 M2: use felixMode (sidebar store) AND felixOpen together so a
  // direct Zustand setState({ open: true }) without calling toggle() does not
  // shift main while Topbar/AIB stay put (split-brain layout).
  const felixMode = useSidebarStore((s) => s.felixMode);

  // Drive ``--sidebar-width`` from the sidebar store so the collapsed
  // (rail) state cascades to the <aside> (w-[var(--sidebar-width)]), Felix,
  // Topbar, and main content offsets.
  // Expanded = 304px, collapsed (icon-only rail) = 72px.
  //
  // MUST be useLayoutEffect, not useEffect: the sidebar's inner content
  // switches to the icon-only layout SYNCHRONOUSLY from the `collapsed`
  // store value during render, but the container width is read from this
  // CSS var at paint time. A post-paint useEffect set the var one frame
  // LATE, so for a single frame the new layout painted against the stale
  // width — on expand, full-width labels rendered inside the 72px rail and
  // wrapped, producing the "icons stacked on top of each other" bug.
  // useLayoutEffect commits the var BEFORE paint, in the same commit as the
  // collapsed re-render, so width and layout change together (no race).
  // No left nav on this page (Operate, Broadcast, Import: the canvas is the work surface): the
  // width is 0, so the main content, Topbar and Felix offsets don't keep an empty column where
  // the nav would be (ISW demo sweep, 31 Oct: a blank 304px band beside Operate).
  const noLeftNav = !showShelfSidebar && suppressSidebar;
  useLayoutEffect(() => {
    const width = noLeftNav ? "0px" : collapsed ? "72px" : "304px";
    document.documentElement.style.setProperty("--sidebar-width", width);
  }, [collapsed, noLeftNav]);

  // Desktop left margin: sidebar width + Felix panel width when open,
  // so Felix pushes main content instead of covering it. Previously
  // this used `${felixWidth}px` only, which caused Felix to overlap
  // the left edge of every page.
  // Mobile is always 0 — sidebar is a drawer overlay.
  const mainMarginLeft = felixMode && felixOpen
    ? `calc(var(--sidebar-width) + ${felixWidth}px)`
    : "var(--sidebar-width)";

  const mainStyle = {
    "--desktop-ml": mainMarginLeft,
  } as React.CSSProperties;

  return (
    <div
      className={
        frontierOn
          ? "min-h-screen bg-transparent"
          : "min-h-screen bg-[var(--color-surface-secondary)]"
      }
      data-uimode={frontierOn ? "frontier" : "classic"}
    >
      {/* Platform-wide Frontier ambient skin — every page sits on it. */}
      {frontierOn && <FrontierBackdrop />}
      {/* Skip to content — accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-lg focus:bg-brand-600 focus:text-white focus:shadow-lg focus:outline-none"
      >
        Skip to main content
      </a>
      {iteratorImmersive ? null : showShelfSidebar ? (
        <ShelfSidebar
          projectId={projectId}
          dwellMs={uiFriction?.shelf_sidebar_dwell_ms}
        />
      ) : suppressSidebar ? null : (
        <FelixRail />
      )}
      <Topbar />
      <CommandPalette />
      <ConversationPanel />
      <ErrorBoundary
        fallback={
          <div className="fixed bottom-4 left-4 z-50 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 p-3 shadow-lg max-w-xs">
            <p className="text-xs text-red-600 dark:text-red-400">Felix encountered an error. Refresh the page to restore the chat.</p>
          </div>
        }
      >
        <FelixChat />
      </ErrorBoundary>
      <ActivityToasts />
      <KeyboardShortcutsHelp />
      {/* OUTSIDE the boundary is where this used to sit, and that is why a
          partial /briefing response blanked the entire shell rather than one
          bar. FelixChat above and the page children below are both wrapped;
          this was not. Degrading to nothing is the correct failure for an
          ambient hint -- it has no content of its own to lose. */}
      {/* `fallback={<></>}`, NOT `fallback={null}`. ErrorBoundary tests the prop
          for truthiness (`if (this.props.fallback)`), so a null fallback falls
          through to the DEFAULT one -- a min-h-[400px] centred error panel,
          which is the last thing an ambient hint bar should put on the page.
          An empty fragment is truthy and renders nothing. */}
      <ErrorBoundary fallback={<></>}>
        <AmbientIntelligenceBar />
      </ErrorBoundary>
      <AppOnboardingTour />

      {/* Main content area */}
      <main
        id="main-content"
        role="main"
        aria-label="Page content"
        className={`min-h-screen ${frontierOn ? "bg-transparent" : "bg-[var(--color-surface-secondary)]"} pt-[calc(var(--topbar-height)_+_var(--ambient-bar-height,0px))] pb-[60px] lg:pb-0 transition-all duration-300 ${iteratorImmersive ? "" : "main-content-margin"}`}
        style={iteratorImmersive ? undefined : mainStyle}
      >
        <AnimatePresence mode="wait">
          <PageTransition key={location.pathname} className={iteratorImmersive ? "" : "p-3 sm:p-4 lg:p-6"}>
            <ErrorBoundary>{children}</ErrorBoundary>
          </PageTransition>
        </AnimatePresence>
      </main>

      {/* Mobile bottom navigation */}
      <MobileBottomNav />
    </div>
  );
}
